import { createFileRoute } from '@tanstack/react-router';
import { assertPublicToolsEnabled } from '@/lib/public-tools-feature';
import { SeoLayout } from '@/components/astro/new-design-25/SEO/seo-layout';
import { seo } from '@/seo/seo';

const heroTitle = 'The Million-Dollar Audio Review Revolution';
const heroDescription =
  'Listen to grant proposal drafts with natural-sounding audio to review flow, clarity, and section transitions before submission.';

const featureTitle =
  'We are not another GrantSpace or Instrument. We are the grant success audio solution that helps';
const featureSubtitle = 'Academic Dyslexia Support';
const featureDescription = '';
const featureBadge = 'Grant Success';
const featureVariant = 'default';
const featureCtaText = 'Try Grant Proposal Audio Review Tool';

const featureFeatures = [
  {
    title: 'Self-Guided Persuasion Review',
    description:
      'Principal Investigators listen to proposal drafts while deciding what needs revision',
  },
  {
    title: 'Efficient Proposal Review',
    description:
      'Research administrators review multi-million dollar proposals efficiently',
  },
  {
    title: 'Argument Clarity',
    description:
      'Grant writing teams listen back to proposal wording and review argument clarity themselves',
  },
  {
    title: 'International Funding',
    description:
      'International researchers perfect proposal language and flow for US funding agencies',
  },
];

const useCasesData = {
  title: 'Use Case Scenarios',
  description:
    'Real-world scenarios where grant proposal audio review transforms research funding success.',
  useCases: [
    {
      title: 'Principal Investigator Self-Review',
      challenge:
        '15-page NSF proposal needs flow and clarity assessment before submission',
      solutionSections: [
        {
          label: 'Audio Review Solution',
          content:
            'Listen to specific aims and research strategy while reviewing argument clarity',
        },
        {
          label: 'Traditional Problem',
          content:
            'Silent reading misses persuasion gaps that review panels will catch',
        },
      ],
    },
    {
      title: 'Research Team Collaboration',
      challenge:
        'Multi-institutional team needs to review and coordinate 25-page NIH application',
      solutionSections: [
        {
          label: 'Audio Review Solution',
          content:
            'Share audio versions of proposal sections for remote team feedback',
        },
        {
          label: 'Traditional Problem',
          content:
            'Email exchanges and tracked changes create confusion and version conflicts',
        },
      ],
    },
    {
      title: 'Grant Office Quality Control',
      challenge:
        'University research office needs to review dozens of proposals before deadlines',
      solutionSections: [
        {
          label: 'Audio Review Solution',
          content:
            'Listen through proposal sections while checking them against office review criteria',
        },
        {
          label: 'Traditional Problem',
          content:
            'Reading dozens of long proposals leads to reviewer fatigue and missed issues',
        },
      ],
    },
    {
      title: 'International PI Language Support',
      challenge:
        'Non-native English speakers need to perfect proposal language and flow',
      solutionSections: [
        {
          label: 'Audio Review Solution',
          content:
            'Hear proposal wording read aloud while reviewing phrasing and flow',
        },
        {
          label: 'Traditional Problem',
          content:
            "Technical writing skills don't translate to persuasive grant narratives",
        },
      ],
    },
  ],
};

const solutionBenefitsData = {
  title: 'Feature Bullets',
  items: [
    {
      text: 'Listen to grant proposals while reviewing persuasion and flow',
    },
    {
      text: 'Hear technical terminology read aloud with natural-sounding voices',
    },
    {
      text: 'Convert reviewer feedback into organized audio guidance',
    },
    {
      text: 'Navigate detected sections in uploaded proposals',
    },
  ],
};

const comparisonTableData = {
  title: 'Comparison Table',
  description:
    'Compare grant proposal review tools and their effectiveness for improving funding success rates.',
  columns: [
    {
      header: 'Grant Audio Review',
      subheader: 'Listening',
      isHighlighted: true,
    },
    { header: 'GrantSpace', isHighlighted: false },
    { header: 'Instrument', isHighlighted: false },
    { header: 'Word Comments', isHighlighted: false },
  ],
  rows: [
    {
      feature: 'Self-Guided Audio Review',
      description:
        'Listen to proposal narration while assessing flow and clarity yourself.',
      values: [
        {
          text: 'Audio playback supports your own review',
          icon: 'check' as const,
        },
        { text: 'Database/search focus', icon: 'close' as const },
        { text: 'Funding discovery focus', icon: 'close' as const },
        { text: 'Manual review required', icon: 'close' as const },
      ],
    },
    {
      feature: 'Technical Terminology',
      description:
        'Accurate pronunciation of scientific and technical terms in grant proposals.',
      values: [
        {
          text: 'Natural-sounding playback of proposal text',
          icon: 'check' as const,
        },
        { text: 'No pronunciation support', icon: 'close' as const },
        { text: 'Text-based only', icon: 'close' as const },
        { text: 'No audio support', icon: 'close' as const },
      ],
    },
    {
      feature: 'Large Document Review',
      description:
        'Efficient review capabilities for comprehensive grant proposals.',
      values: [
        {
          text: 'Efficient audio scanning of 15-25 page proposals',
          icon: 'check' as const,
        },
        { text: 'Not designed for document review', icon: 'close' as const },
        { text: 'Not designed for document review', icon: 'close' as const },
        { text: 'Tedious for long documents', icon: 'close' as const },
      ],
    },
    {
      feature: 'Collaborative Features',
      description:
        'Team collaboration capabilities for multi-institutional grant teams.',
      values: [
        {
          text: 'Audio feedback integration for research teams',
          icon: 'check' as const,
        },
        { text: 'Basic collaboration', icon: 'warning' as const },
        { text: 'No collaboration tools', icon: 'close' as const },
        { text: 'Comments system', icon: 'check' as const },
      ],
    },
    {
      feature: 'Proposal Navigation',
      description: 'Section-by-section playback for long grant proposals.',
      values: [
        {
          text: 'Navigate detected document sections',
          icon: 'check' as const,
        },
        { text: 'Grant writing templates', icon: 'check' as const },
        { text: 'Funding database only', icon: 'close' as const },
        { text: 'Generic document features', icon: 'close' as const },
      ],
    },
    {
      feature: 'Cost for Research Teams',
      description:
        'Pricing structure and accessibility for academic research teams and institutions.',
      values: [
        { text: 'Affordable for academic budgets', icon: 'check' as const },
        { text: 'Expensive subscription', icon: 'close' as const },
        { text: 'Expensive subscription', icon: 'close' as const },
        { text: 'Included with Office', icon: 'check' as const },
      ],
    },
  ],
  showIcons: true,
  ariaLabel: 'Grant Proposal Audio Review Comparison Table',
};

const seoSections = [
  {
    title: 'Review Grant Proposals by Listening Back',
    content:
      'Audio playback gives grant writers another way to review proposal wording, transitions, and narrative flow. When you listen to your specific aims or research strategy, you can decide where the draft needs revision while following along with the source text. Listening automates the read-aloud step for long NSF, NIH, and other funding applications; the writer remains responsible for evaluating the proposal.',
    contentSecondary:
      'Listening converts the uploaded proposal text into natural-sounding narration so you can review the document away from your desk or while following along. It complements writing, grammar, and collaboration tools without scoring arguments or recommending proposal changes.',
  },
  {
    title: 'Review Each Proposal Section by Audio',
    content:
      'Upload a proposal and Listening converts its source text into audio with navigation for detected document sections. It does not evaluate persuasive effectiveness, recognize funder requirements, or identify weak arguments automatically.',
    contentSecondary:
      "Research teams can also convert written reviewer feedback into audio and listen while reviewing the relevant proposal sections. Whether you're preparing an NSF, NIH, DOE, or private foundation submission, Listening provides document playback while your team supplies the grant-writing judgment.",
  },
];

export const Route = createFileRoute(
  '/_marketing/_seo/grant-proposal-audio-review-tool',
)({
  beforeLoad: assertPublicToolsEnabled,
  head: () => ({
    meta: [
      ...seo({
        title: 'Grant Proposal Audio Review Tool | Listening',
        description:
          'Grant proposal audio review tool. Listen to your proposals to improve flow, clarity, and persuasion. Perfect for NSF, NIH, and research funding applications.',
      }),
    ],
  }),
  component: () => (
    <SeoLayout
      comparisonTableData={comparisonTableData}
      featureBadge={featureBadge}
      featureCtaText={featureCtaText}
      featureDescription={featureDescription}
      featureFeatures={featureFeatures}
      featureSubtitle={featureSubtitle}
      featureTitle={featureTitle}
      featureVariant={featureVariant}
      pageDescription={heroDescription}
      pageTitle={heroTitle}
      seoSections={seoSections}
      solutionBenefitsData={solutionBenefitsData}
      useCasesData={useCasesData}
    />
    // Finish this page
  ),
});
